import React, { Suspense, useEffect } from 'react';
import { ConfigProvider, theme as antTheme, App as AntApp } from 'antd';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import zhCN from 'antd/locale/zh_CN';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';

import { useThemeStore } from '@stores/themeStore';
import { useAuthStore } from '@stores/authStore';
import { useNotificationStore } from '@stores/notificationStore';
import { usePermissionStore } from '@stores/permissionStore';
import { Router } from '@components/layout/Router';
import { GlobalSpin } from '@components/common/GlobalSpin';

import '@styles/global.css';

// 设置 dayjs 语言
dayjs.locale('zh-cn');

// 创建 QueryClient
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5分钟
    },
  },
});

const App: React.FC = () => {
  const { isDark } = useThemeStore();
  const { checkAuth, token } = useAuthStore();
  const { connect } = useNotificationStore();
  const { initialize: initializePermissions } = usePermissionStore();

  // 初始化时检查登录状态并连接 WebSocket
  useEffect(() => {
    checkAuth();
    initializePermissions();
  }, [checkAuth, initializePermissions]);

  // 当 token 存在时，连接 WebSocket
  useEffect(() => {
    if (token) {
      connect(token).catch(console.error);
    }
  }, [token, connect]);

  // Ant Design 主题配置
  const antdTheme = {
    algorithm: isDark ? antTheme.darkAlgorithm : antTheme.defaultAlgorithm,
    token: {
      colorPrimary: '#1890ff',
      borderRadius: 6,
      colorSuccess: '#52c41a',
      colorWarning: '#faad14',
      colorError: '#f5222d',
      fontSize: 14,
    },
    components: {
      Layout: {
        headerHeight: 64,
        headerPadding: '0 24px',
        siderWidth: 220,
      },
      Menu: {
        iconSize: 16,
        itemHeight: 44,
      },
      Table: {
        headerBg: isDark ? '#1f1f1f' : '#fafafa',
        rowHoverBg: isDark ? '#2a2a2a' : '#e6f7ff',
      },
    },
  };

  return (
    <QueryClientProvider client={queryClient}>
      <ConfigProvider locale={zhCN} theme={antdTheme}>
        <AntApp>
          <Suspense fallback={<GlobalSpin />}>
            <Router />
          </Suspense>
        </AntApp>
      </ConfigProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
};

export default App;
